'use client'

import { PlayIcon, XIcon } from '@phosphor-icons/react'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

import { Heading } from '@/components/primitives/Heading/Heading'
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { asHttpSrc } from '@/lib/utils'

import type { ContentfulAsset } from '@/lib/contentful-api'

export interface VideoCardType {
  image?: ContentfulAsset
  video?: ContentfulAsset
  title?: string
}

export const VideoCard = ({ image, video, title }: VideoCardType) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [open, setOpen] = useState(false)
  const [videoError, setVideoError] = useState(false)
  const [videoLoading, setVideoLoading] = useState(true)

  const videoUrl = asHttpSrc(video?.fields?.file?.url) ?? ''
  const imageUrl = asHttpSrc(image?.fields?.file?.url) ?? ''

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {
        setVideoError(true)
      })
      setIsPlaying(true)
    }
  }

  const handleVideoError = () => {
    setVideoError(true)
    setVideoLoading(false)
  }

  const handleVideoLoad = () => {
    setVideoLoading(false)
    setVideoError(false)
  }

  const handleVideoCanPlay = () => {
    setVideoLoading(false)
  }

  useEffect(() => {
    if (open) {
      // Reset states when dialog opens
      setVideoError(false)
      setVideoLoading(true)

      if (videoRef.current) {
        videoRef.current.play().catch(() => {
          setVideoError(true)
        })
        setIsPlaying(true)
      }
    } else if (videoRef.current) {
      videoRef.current.pause()
      setIsPlaying(false)
    }
  }, [open])

  return (
    <div className="flex h-full flex-col">
      <div className="relative aspect-[4/3] w-full">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <div className="size-full">
              {imageUrl ? (
                <Image
                  className="size-full rounded-t-xl object-cover"
                  src={imageUrl}
                  alt={title || 'Video thumbnail'}
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
                />
              ) : videoUrl ? (
                <video className="size-full rounded-t-xl object-cover" src={videoUrl} />
              ) : null}
              <button className="absolute inset-0 !z-[99999] flex items-center justify-center will-change-transform" title="Play Video">
                <PlayIcon size={48} color="white" />
              </button>
            </div>
          </DialogTrigger>
          <DialogContent hideClose className="min-h-72 gap-y-2 border-none p-0  px-4  md:min-w-[800px] [&>button[data-dialog-close]]:hidden">
            <DialogTitle className="hidden" />
            <DialogDescription className="hidden" />
            <div className="overflow-hidden rounded-xl border border-grey-200 bg-grey-100">
              <div className="mb-2 flex border-b border-grey-200 bg-white px-6 py-5">
                <Heading className="flex-1 text-grey-900" as={'h3'} variant="secondary">
                  {title}
                </Heading>
                <button title="Close" onClick={() => setOpen(false)}>
                  <XIcon className="size-5 cursor-pointer text-grey-500" />
                </button>
              </div>
              <div className="relative bg-white p-6">
                {videoError ? (
                  <div className="flex h-64 items-center justify-center bg-gray-100">
                    <p className="text-gray-500">Video failed to load</p>
                  </div>
                ) : (
                  <>
                    <video
                      ref={videoRef}
                      className="size-full"
                      src={videoUrl}
                      poster={imageUrl}
                      controls
                      preload="metadata"
                      playsInline
                      webkit-playsinline="true"
                      onError={handleVideoError}
                      onLoadedData={handleVideoLoad}
                      onCanPlay={handleVideoCanPlay}
                      onLoadStart={() => setVideoLoading(true)}
                    />
                    {videoLoading && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black bg-opacity-50">
                        <div className="mb-2 size-8 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                        <div className="text-white">Loading video...</div>
                      </div>
                    )}
                    {!isPlaying && !videoLoading && !videoError && (
                      <button className="absolute inset-0 flex items-center justify-center" title="Play Video" onClick={handlePlay}>
                        <PlayIcon size={48} color="white" />
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="h-full flex-1 bg-white ">
        <p className="p-4 text-lg font-semibold leading-6 md:px-6 md:py-5 md:text-xl">{title}</p>
      </div>
    </div>
  )
}
